/**
 * useHistoryTracking Hook
 *
 * Automatically tracks timeline actions in the history store:
 * - Records action on tool add/remove/move/update
 * - Records action on tab operations
 * - Clears history on tab switch
 * - Implements state snapshot system
 *
 * Updated for Parametric Animation Refactor:
 * - Uses targetRotation and easing instead of speed/intensity
 */

import { useEffect, useRef } from 'react';
import { useSceneStore } from '../stores/sceneStore';
import { useTimelineStore } from '../stores/timelineStore';
import { useHistoryStore, ActionType } from '../stores/historyStore';

export function useHistoryTracking() {
  const activeTabId = useSceneStore((state) => state.activeTabId);
  const previousTabId = useRef<string | null>(null);

  useEffect(() => {
    // Clear history when switching tabs
    if (previousTabId.current && previousTabId.current !== activeTabId) {
      if (activeTabId) {
        useHistoryStore.getState().clearHistory(activeTabId);
      }
    }
    previousTabId.current = activeTabId;
  }, [activeTabId]);

  useEffect(() => {
    // Subscribe to timeline store changes
    const unsubscribe = useTimelineStore.subscribe((state, prevState) => {
      if (!activeTabId) return;

      const recordAction = useHistoryStore.getState().recordAction;

      const makeSnapshot = () => ({
        tools: [...state.tools],
        tracks: [...state.tracks],
        groups: [...state.groups],
        placedGroups: [...state.placedGroups],
        duration: state.duration,
        playheadPosition: state.playheadPosition,
      });

      // Check for tool additions
      if (state.tools.length > prevState.tools.length) {
        recordAction(activeTabId, {
          timestamp: new Date().toISOString(),
          actionType: ActionType.TOOL_ADD,
          description: 'Added tool',
          timelineSnapshot: makeSnapshot(),
        });
      }

      // Check for tool removals
      if (state.tools.length < prevState.tools.length) {
        recordAction(activeTabId, {
          timestamp: new Date().toISOString(),
          actionType: ActionType.TOOL_DELETE,
          description: 'Removed tool',
          timelineSnapshot: makeSnapshot(),
        });
      }

      // Check for tool moves (position or track changes)
      if (state.tools.length === prevState.tools.length) {
        const hasPositionChanges = state.tools.some((tool, index) => {
          const prevTool = prevState.tools[index];
          return (
            prevTool &&
            (tool.startTime !== prevTool.startTime ||
              tool.trackNumber !== prevTool.trackNumber)
          );
        });

        if (hasPositionChanges) {
          recordAction(activeTabId, {
            timestamp: new Date().toISOString(),
            actionType: ActionType.TOOL_MOVE,
            description: 'Moved tool',
            timelineSnapshot: makeSnapshot(),
          });
        }

        // Check for tool property updates
        const hasPropertyChanges = state.tools.some((tool, index) => {
          const prevTool = prevState.tools[index];
          return (
            prevTool &&
            tool.id === prevTool.id &&
            (tool.duration !== prevTool.duration ||
              tool.targetRotation !== prevTool.targetRotation ||
              tool.easing !== prevTool.easing)
          );
        });

        if (hasPropertyChanges) {
          recordAction(activeTabId, {
            timestamp: new Date().toISOString(),
            actionType: ActionType.TOOL_UPDATE,
            description: 'Updated tool properties',
            timelineSnapshot: makeSnapshot(),
          });
        }
      }

      // Check for group definition additions/removals
      if (state.groups.length > prevState.groups.length) {
        recordAction(activeTabId, {
          timestamp: new Date().toISOString(),
          actionType: ActionType.GROUP_ADD,
          description: 'Created group',
          timelineSnapshot: makeSnapshot(),
        });
      }
      if (state.groups.length < prevState.groups.length) {
        recordAction(activeTabId, {
          timestamp: new Date().toISOString(),
          actionType: ActionType.GROUP_DELETE,
          description: 'Deleted group',
          timelineSnapshot: makeSnapshot(),
        });
      }

      // Check for placed group additions/removals/moves
      if (state.placedGroups.length > prevState.placedGroups.length) {
        recordAction(activeTabId, {
          timestamp: new Date().toISOString(),
          actionType: ActionType.PLACED_GROUP_ADD,
          description: 'Placed group on timeline',
          timelineSnapshot: makeSnapshot(),
        });
      }
      if (state.placedGroups.length < prevState.placedGroups.length) {
        recordAction(activeTabId, {
          timestamp: new Date().toISOString(),
          actionType: ActionType.PLACED_GROUP_DELETE,
          description: 'Removed placed group',
          timelineSnapshot: makeSnapshot(),
        });
      }
      if (
        state.placedGroups.length === prevState.placedGroups.length &&
        state.placedGroups.length > 0
      ) {
        const hasPGMoves = state.placedGroups.some((pg, i) => {
          const prev = prevState.placedGroups[i];
          return (
            prev &&
            (pg.startTime !== prev.startTime ||
              pg.trackNumber !== prev.trackNumber)
          );
        });
        if (hasPGMoves) {
          recordAction(activeTabId, {
            timestamp: new Date().toISOString(),
            actionType: ActionType.PLACED_GROUP_MOVE,
            description: 'Moved placed group',
            timelineSnapshot: makeSnapshot(),
          });
        }
      }
    });

    return unsubscribe;
  }, [activeTabId]);

  useEffect(() => {
    // Subscribe to scene store tab operations
    const unsubscribe = useSceneStore.subscribe((state, prevState) => {
      if (!activeTabId) return;

      const recordAction = useHistoryStore.getState().recordAction;
      const timelineState = useTimelineStore.getState();

      // Check for tab operations (create, delete, rename, reorder)
      if (
        state.tabs.length !== prevState.tabs.length ||
        state.tabs.some((tab, index) => {
          const prevTab = prevState.tabs[index];
          return (
            prevTab &&
            (tab.name !== prevTab.name || tab.order !== prevTab.order)
          );
        })
      ) {
        const snapshot = {
          timestamp: new Date().toISOString(),
          actionType: ActionType.TAB_OPERATION,
          description: 'Tab operation',
          timelineSnapshot: {
            tools: [...timelineState.tools],
            tracks: [...timelineState.tracks],
            groups: [...timelineState.groups],
            placedGroups: [...timelineState.placedGroups],
            duration: timelineState.duration,
            playheadPosition: timelineState.playheadPosition,
          },
          sceneSnapshot: {
            tabs: [...state.tabs],
            activeTabId: state.activeTabId,
          },
        };
        recordAction(activeTabId, snapshot);
      }
    });

    return unsubscribe;
  }, [activeTabId]);
}
